/**
 * Monthly Targets Editor
 * ======================
 * Enhanced targets editor with:
 * - Monthly/Weekly view toggle
 * - 12 month tabs (Jan through Dec) OR weekly tabs
 * - Market-level targeting
 * - Editable targets per month and per week
 * - Year View modal showing annual aggregate
 * - Year-End Projection based on current period targets
 * - Quick copy from one period to others
 */
import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import { Progress } from '../ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Tabs, TabsList, TabsTrigger } from '../ui/tabs';
import {
  Save,
  Loader2,
  DollarSign,
  Users,
  Target,
  TrendingUp,
  Calendar,
  Clock,
  BarChart3,
  Copy,
  ChevronLeft,
  ChevronRight,
  Eye,
  CheckCircle2,
  AlertCircle,
  Building2,
  MapPin,
  CalendarDays,
  CalendarRange,
  Gauge,
  Percent,
  Activity,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const MONTHS = [
  { key: 'jan', name: 'January', short: 'Jan' },
  { key: 'feb', name: 'February', short: 'Feb' },
  { key: 'mar', name: 'March', short: 'Mar' },
  { key: 'apr', name: 'April', short: 'Apr' },
  { key: 'may', name: 'May', short: 'May' },
  { key: 'jun', name: 'June', short: 'Jun' },
  { key: 'jul', name: 'July', short: 'Jul' },
  { key: 'aug', name: 'August', short: 'Aug' },
  { key: 'sep', name: 'September', short: 'Sep' },
  { key: 'oct', name: 'October', short: 'Oct' },
  { key: 'nov', name: 'November', short: 'Nov' },
  { key: 'dec', name: 'December', short: 'Dec' },
];

// Generate weeks for the year (1-52)
const WEEKS = Array.from({ length: 52 }, (_, i) => ({
  key: `week_${i + 1}`,
  name: `Week ${i + 1}`,
  short: `W${i + 1}`,
  number: i + 1
}));

// Default targets - $25M annual / 12 months = $2,083,333.33 per month
// Each metric has a target (goal) and minimum (floor)
const BASELINE_MONTHLY_TARGETS = {
  leads: 867,
  leads_min: 700,
  appointments: 379,
  appointments_min: 300,
  issued: 306,
  issued_min: 245,
  demos: 256,
  demos_min: 205,
  sales: 100,
  sales_min: 80,
  revenue: 2083333.33, // $25M / 12 months
  revenue_min: 1666666.67, // 80% of target
  spend: 362000,
  spend_max: 400000, // Max budget (higher = worse for spend)
  set_rate: 43.7,
  set_rate_min: 35.0,
  issue_rate: 80.9,
  issue_rate_min: 70.0,
  demo_rate: 82.1,
  demo_rate_min: 70.0,
  close_rate: 39.2,
  close_rate_min: 30.0,
  average_ticket: 24040.40
};

// Weekly baseline (~1/4 of monthly)
const BASELINE_WEEKLY_TARGETS = {
  leads: 217,
  leads_min: 175,
  appointments: 95,
  appointments_min: 75,
  issued: 77,
  issued_min: 61,
  demos: 64,
  demos_min: 51,
  sales: 25,
  sales_min: 20,
  revenue: 520833.33, // $25M / 48 weeks
  revenue_min: 416666.67,
  spend: 90500,
  spend_max: 100000,
};

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value || 0);
};

const formatNumber = (value) => {
  return new Intl.NumberFormat('en-US').format(value || 0);
};

// Year-End Projection Panel (Editable)
const YearEndProjection = ({ 
  annualTargets, 
  annualMins,
  onAnnualTargetsChange,
  onAnnualMinsChange,
  canEdit = true 
}) => {
  const [isEditing, setIsEditing] = useState(false);
  
  // Calculate formatted values
  const formatCompact = (value) => {
    if (value >= 1000000) {
      return `$${(value / 1000000).toFixed(1)}M`;
    } else if (value >= 1000) {
      return `$${(value / 1000).toFixed(0)}K`;
    }
    return formatCurrency(value);
  };
  
  const handleTargetChange = (field, value) => {
    onAnnualTargetsChange?.({
      ...annualTargets,
      [field]: parseFloat(value) || 0
    });
  };
  
  const handleMinChange = (field, value) => {
    onAnnualMinsChange?.({
      ...annualMins,
      [field]: parseFloat(value) || 0
    });
  };
  
  // All metrics for targets
  const volumeMetrics = [
    { key: 'leads', label: 'New Opportunities', icon: Users, placeholder: '10404', minPlaceholder: '8323' },
    { key: 'appointments', label: 'Appointments Set', icon: Calendar, placeholder: '4548', minPlaceholder: '3638' },
    { key: 'issued', label: 'Leads Issued', icon: Target, placeholder: '3672', minPlaceholder: '2938' },
    { key: 'demos', label: 'Demos', icon: Eye, placeholder: '2600', minPlaceholder: '2080' },
    { key: 'sales', label: 'Jobs Sold', icon: TrendingUp, placeholder: '1200', minPlaceholder: '960' },
    { key: 'payments_received', label: 'Payments Received', icon: CheckCircle2, placeholder: '1000', minPlaceholder: '800' },
  ];
  
  const revenueMetrics = [
    { key: 'revenue', label: 'Sold Jobs Amount', icon: DollarSign, placeholder: '25000000', minPlaceholder: '25000000', isCurrency: true },
    { key: 'revenue_collected', label: 'Revenue Amount', icon: DollarSign, placeholder: '21250000', minPlaceholder: '21250000', isCurrency: true },
  ];
  
  const marketingMetrics = [
    { key: 'ad_spend', label: 'Ad Spend Budget', icon: DollarSign, placeholder: '4200000', minPlaceholder: '4800000', isCurrency: true },
    { key: 'cpl', label: 'Cost Per Lead', icon: Percent, placeholder: '485', minPlaceholder: '405', isCurrency: true },
    { key: 'roas', label: 'Return on Spend', icon: Activity, placeholder: '5.0', minPlaceholder: '4.0', isMultiple: true },
  ];
  
  return (
    <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/30 dark:to-indigo-950/30 rounded-lg border border-blue-200 dark:border-blue-800">
      <div className="flex items-center gap-2 mb-3">
        <TrendingUp className="h-4 w-4 text-blue-600" />
        <span className="font-semibold text-blue-700 dark:text-blue-300">Annual Goals</span>
        {canEdit && (
          <Button 
            variant="ghost" 
            size="sm" 
            className="ml-auto h-7 text-xs"
            onClick={() => setIsEditing(!isEditing)}
            data-testid="edit-annual-goals-btn"
          >
            {isEditing ? (
              <>
                <CheckCircle2 className="h-3 w-3 mr-1" />
                Done
              </>
            ) : (
              <>
                <Target className="h-3 w-3 mr-1" />
                Edit Goals
              </>
            )}
          </Button>
        )}
      </div>
      
      {isEditing ? (
        // Editable Mode
        <div className="space-y-4">
          {/* Volume Metrics */}
          <div>
            <h6 className="text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wider">Volume Metrics</h6>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {volumeMetrics.map(metric => (
                <div key={metric.key} className="bg-white/50 dark:bg-slate-900/50 p-3 rounded-lg space-y-2">
                  <Label className="text-xs text-muted-foreground flex items-center gap-1">
                    <metric.icon className="h-3 w-3" />
                    {metric.label}
                  </Label>
                  <Input
                    type="number"
                    value={annualTargets[metric.key] || ''}
                    onChange={(e) => handleTargetChange(metric.key, e.target.value)}
                    className="h-8 text-sm"
                    placeholder={metric.placeholder}
                  />
                  <div>
                    <span className="text-xs text-orange-500">Minimum</span>
                    <Input
                      type="number"
                      value={annualMins[metric.key] || ''}
                      onChange={(e) => handleMinChange(metric.key, e.target.value)}
                      className="h-7 text-xs border-orange-200"
                      placeholder={metric.minPlaceholder}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Revenue Metrics */}
          <div>
            <h6 className="text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wider">Revenue Metrics</h6>
            <div className="grid grid-cols-2 gap-3">
              {revenueMetrics.map(metric => (
                <div key={metric.key} className="bg-white/50 dark:bg-slate-900/50 p-3 rounded-lg space-y-2">
                  <Label className="text-xs text-muted-foreground flex items-center gap-1">
                    <metric.icon className="h-3 w-3" />
                    {metric.label}
                  </Label>
                  <Input
                    type="number"
                    value={annualTargets[metric.key] || ''}
                    onChange={(e) => handleTargetChange(metric.key, e.target.value)}
                    className="h-8 text-sm"
                    placeholder={metric.placeholder}
                  />
                  <div>
                    <span className="text-xs text-orange-500">Minimum</span>
                    <Input
                      type="number"
                      value={annualMins[metric.key] || ''}
                      onChange={(e) => handleMinChange(metric.key, e.target.value)}
                      className="h-7 text-xs border-orange-200"
                      placeholder={metric.minPlaceholder}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Marketing Metrics */}
          <div>
            <h6 className="text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wider">Marketing Efficiency</h6>
            <div className="grid grid-cols-3 gap-3">
              {marketingMetrics.map(metric => (
                <div key={metric.key} className="bg-white/50 dark:bg-slate-900/50 p-3 rounded-lg space-y-2">
                  <Label className="text-xs text-muted-foreground flex items-center gap-1">
                    <metric.icon className="h-3 w-3" />
                    {metric.label}
                  </Label>
                  <Input
                    type="number"
                    step={metric.isMultiple ? "0.1" : "1"}
                    value={annualTargets[metric.key] || ''}
                    onChange={(e) => handleTargetChange(metric.key, e.target.value)}
                    className="h-8 text-sm"
                    placeholder={metric.placeholder}
                  />
                  <div>
                    <span className="text-xs text-orange-500">Minimum</span>
                    <Input
                      type="number"
                      step={metric.isMultiple ? "0.1" : "1"}
                      value={annualMins[metric.key] || ''}
                      onChange={(e) => handleMinChange(metric.key, e.target.value)}
                      className="h-7 text-xs border-orange-200"
                      placeholder={metric.minPlaceholder}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : (
        // Display Mode
        <div className="space-y-4">
          {/* Volume Metrics Row */}
          <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
            {volumeMetrics.map(metric => (
              <div key={metric.key} className="bg-white/50 dark:bg-slate-900/50 p-2 rounded-lg">
                <p className="text-[10px] text-muted-foreground">{metric.label}</p>
                <p className="text-sm font-bold">{formatNumber(annualTargets[metric.key] || 0)}</p>
                <p className="text-[10px] text-orange-500">Min: {formatNumber(annualMins[metric.key] || 0)}</p>
              </div>
            ))}
          </div>
          
          {/* Revenue & Marketing Row */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            {revenueMetrics.map(metric => (
              <div key={metric.key} className="bg-white/50 dark:bg-slate-900/50 p-2 rounded-lg">
                <p className="text-[10px] text-muted-foreground">{metric.label}</p>
                <p className="text-sm font-bold text-emerald-600">{formatCompact(annualTargets[metric.key] || 0)}</p>
                <p className="text-[10px] text-orange-500">Min: {formatCompact(annualMins[metric.key] || 0)}</p>
              </div>
            ))}
            {marketingMetrics.map(metric => (
              <div key={metric.key} className="bg-white/50 dark:bg-slate-900/50 p-2 rounded-lg">
                <p className="text-[10px] text-muted-foreground">{metric.label}</p>
                <p className="text-sm font-bold">
                  {metric.isCurrency 
                    ? formatCompact(annualTargets[metric.key] || 0)
                    : metric.isMultiple 
                      ? `${(annualTargets[metric.key] || 0).toFixed(1)}x`
                      : formatNumber(annualTargets[metric.key] || 0)
                  }
                </p>
                <p className="text-[10px] text-orange-500">
                  Min: {metric.isCurrency 
                    ? formatCompact(annualMins[metric.key] || 0)
                    : metric.isMultiple 
                      ? `${(annualMins[metric.key] || 0).toFixed(1)}x`
                      : formatNumber(annualMins[metric.key] || 0)
                  }
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Year View Modal Component - Comprehensive & Editable
const YearViewModal = ({ 
  isOpen, 
  onClose, 
  yearData, 
  loading,
  annualTargets,
  annualMins,
  annualMarketTargets,
  onAnnualTargetsChange,
  onAnnualMinsChange,
  onAnnualMarketTargetsChange,
  onSave,
  canEdit = true,
  realtimeMetrics = {}
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  
  if (!isOpen) return null;
  
  // Merge backend YTD totals with realtime metrics for the most up-to-date values
  const backendYtd = yearData?.ytd_totals || {};
  const ytdTotals = {
    ...backendYtd,
    // Use realtime metrics actuals if available (these are more current)
    revenue_collected: realtimeMetrics?.revenue_collected?.actual || backendYtd.revenue_collected || 0,
    payments_received: realtimeMetrics?.payments_received?.actual || backendYtd.payments_received || 0,
    issued: realtimeMetrics?.issued?.actual || backendYtd.issued || 0,
  };
  
  // Calculate default annual targets with sensible defaults
  const effectiveAnnualTargets = {
    ...annualTargets,
    // Default revenue_collected to 85% of revenue if not set
    revenue_collected: annualTargets?.revenue_collected || (annualTargets?.revenue * 0.85) || 0,
  };
  
  const annualProgress = yearData?.annual_progress || {};
  
  const getStatusColor = (progress, expected) => {
    if (progress >= expected * 0.95) return 'text-emerald-600';
    if (progress >= expected * 0.80) return 'text-yellow-600';
    return 'text-red-600';
  };
  
  const formatCompact = (value) => {
    if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
    return formatCurrency(value);
  };
  
  const handleTargetChange = (field, value) => {
    onAnnualTargetsChange?.({
      ...annualTargets,
      [field]: parseFloat(value) || 0
    });
  };
  
  const handleMinChange = (field, value) => {
    onAnnualMinsChange?.({
      ...annualMins,
      [field]: parseFloat(value) || 0
    });
  };
  
  // All metrics definitions
  const volumeMetrics = [
    { key: 'leads', label: 'New Opportunities', icon: Users, color: 'blue' },
    { key: 'appointments', label: 'Appointments Set', icon: Calendar, color: 'green' },
    { key: 'issued', label: 'Leads Issued', icon: Target, color: 'cyan' },
    { key: 'demos', label: 'Demos', icon: Eye, color: 'purple' },
    { key: 'sales', label: 'Jobs Sold', icon: TrendingUp, color: 'emerald' },
    { key: 'payments_received', label: 'Payments Received', icon: CheckCircle2, color: 'teal' },
  ];
  
  const revenueMetrics = [
    { key: 'revenue', label: 'Sold Jobs Amount', icon: DollarSign, color: 'yellow', isCurrency: true },
    { key: 'revenue_collected', label: 'Revenue Collected', icon: DollarSign, color: 'amber', isCurrency: true },
  ];
  
  const marketingMetrics = [
    { key: 'ad_spend', label: 'Ad Spend Budget', icon: DollarSign, color: 'red', isCurrency: true },
    { key: 'cpl', label: 'Cost Per Lead', icon: Percent, color: 'orange', isCurrency: true },
    { key: 'roas', label: 'Return on Spend', icon: Activity, color: 'pink', isMultiple: true },
  ];
  
  const marketsList = [
    { key: 'jacksonville', label: 'Jacksonville', icon: '🦎' },
    { key: 'orlando', label: 'Orlando', icon: '🏰' },
    { key: 'tampa', label: 'Tampa', icon: '⚡' },
    { key: 'west-palm', label: 'West Palm', icon: '🌴' },
    { key: 'naples', label: 'Naples/Ft. Myers', icon: '🌊' },
    { key: 'gainesville', label: 'Gainesville', icon: '🐊' },
  ];
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden" data-testid="year-view-modal">
        <DialogHeader className="pr-8">
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-primary" />
              {yearData?.year || new Date().getFullYear()} Annual Overview
            </DialogTitle>
            <div className="flex items-center gap-2">
              {canEdit && (
                <Button 
                  variant={isEditing ? "default" : "outline"}
                  size="sm"
                  onClick={() => setIsEditing(!isEditing)}
                  data-testid="toggle-year-edit-btn"
                >
                  {isEditing ? (
                    <>
                      <CheckCircle2 className="h-4 w-4 mr-2" />
                      Done Editing
                    </>
                  ) : (
                    <>
                      <Target className="h-4 w-4 mr-2" />
                      Edit Goals
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </DialogHeader>
        
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            {/* Tab Navigation */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-3 mb-4">
                <TabsTrigger value="overview" data-testid="year-tab-overview">
                  <BarChart3 className="h-4 w-4 mr-2" />
                  Overview
                </TabsTrigger>
                <TabsTrigger value="goals" data-testid="year-tab-goals">
                  <Target className="h-4 w-4 mr-2" />
                  Annual Goals
                </TabsTrigger>
                <TabsTrigger value="markets" data-testid="year-tab-markets">
                  <MapPin className="h-4 w-4 mr-2" />
                  By Market
                </TabsTrigger>
              </TabsList>
            </Tabs>
            
            <ScrollArea className="h-[60vh] pr-4">
              {/* OVERVIEW TAB */}
              {activeTab === 'overview' && (
                <div className="space-y-6">
                  {/* Revenue Progress - Featured */}
                  <div className="p-6 bg-gradient-to-br from-primary/10 to-primary/5 rounded-xl border border-primary/20">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">Annual Revenue Goal</p>
                        <p className="text-3xl font-bold text-primary">
                          {formatCurrency(annualTargets?.revenue || 0)}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium text-muted-foreground">YTD Actual</p>
                        <p className="text-2xl font-bold">
                          {formatCurrency(ytdTotals.revenue || 0)}
                        </p>
                      </div>
                    </div>
                    <Progress value={annualProgress.revenue || 0} className="h-3" />
                    <div className="flex justify-between mt-2 text-sm">
                      <span className={getStatusColor(annualProgress.revenue, yearData?.expected_progress_pct)}>
                        {(annualProgress.revenue || 0).toFixed(1)}% achieved
                      </span>
                      <span className="text-muted-foreground">
                        Expected: {yearData?.expected_progress_pct || 0}% ({yearData?.days_elapsed_ytd || 0} days elapsed)
                      </span>
                    </div>
                  </div>
                  
                  {/* Volume Metrics Grid */}
                  <div>
                    <h4 className="font-semibold mb-3 text-sm uppercase tracking-wide text-muted-foreground">Volume Metrics</h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {volumeMetrics.map(({ key, label, icon: Icon, color }) => (
                        <div key={key} className="p-4 rounded-lg border bg-muted/30">
                          <div className="flex items-center gap-2 mb-2">
                            <Icon className={`h-4 w-4 text-${color}-500`} />
                            <span className="text-sm font-medium">{label}</span>
                          </div>
                          <p className="text-xl font-bold">
                            {formatNumber(ytdTotals[key] || 0)} 
                            <span className="text-sm font-normal text-muted-foreground ml-1">
                              / {formatNumber(annualTargets?.[key] || 0)}
                            </span>
                          </p>
                          <Progress value={annualProgress[key] || 0} className="h-2 mt-2" />
                          <p className={`text-xs mt-1 ${getStatusColor(annualProgress[key], yearData?.expected_progress_pct)}`}>
                            {(annualProgress[key] || 0).toFixed(1)}% of goal
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Revenue & Marketing Metrics */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <h4 className="font-semibold mb-3 text-sm uppercase tracking-wide text-muted-foreground">Revenue Metrics</h4>
                      <div className="space-y-3">
                        {revenueMetrics.map(({ key, label, icon: Icon, color }) => (
                          <div key={key} className="p-3 rounded-lg border bg-muted/30 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Icon className={`h-4 w-4 text-${color}-500`} />
                              <span className="text-sm">{label}</span>
                            </div>
                            <div className="text-right">
                              <p className="font-bold">{formatCompact(effectiveAnnualTargets?.[key] || 0)}</p>
                              <p className="text-xs text-muted-foreground">YTD: {formatCompact(ytdTotals[key] || 0)}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div>
                      <h4 className="font-semibold mb-3 text-sm uppercase tracking-wide text-muted-foreground">Marketing Efficiency</h4>
                      <div className="space-y-3">
                        {marketingMetrics.map(({ key, label, icon: Icon, color, isCurrency, isMultiple }) => (
                          <div key={key} className="p-3 rounded-lg border bg-muted/30 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Icon className={`h-4 w-4 text-${color}-500`} />
                              <span className="text-sm">{label}</span>
                            </div>
                            <div className="text-right">
                              <p className="font-bold">
                                {isMultiple 
                                  ? `${(effectiveAnnualTargets?.[key] || 0).toFixed(1)}x`
                                  : isCurrency 
                                    ? formatCompact(effectiveAnnualTargets?.[key] || 0)
                                    : formatNumber(effectiveAnnualTargets?.[key] || 0)
                                }
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  
                  {/* Monthly Breakdown Table */}
                  <div>
                    <h4 className="font-semibold mb-3">Monthly Breakdown</h4>
                    <div className="border rounded-lg overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-muted/50">
                          <tr>
                            <th className="text-left p-3 font-medium">Month</th>
                            <th className="text-right p-3 font-medium">Revenue Target</th>
                            <th className="text-right p-3 font-medium">Actual</th>
                            <th className="text-right p-3 font-medium">Sales Target</th>
                            <th className="text-right p-3 font-medium">Actual</th>
                            <th className="text-center p-3 font-medium">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {yearData?.monthly_data?.map((month) => (
                            <tr 
                              key={month.month} 
                              className={`border-t ${month.is_current ? 'bg-primary/5' : ''} ${month.is_future ? 'text-muted-foreground' : ''}`}
                            >
                              <td className="p-3 font-medium">
                                {month.month_name}
                                {month.is_current && <Badge className="ml-2 text-xs" variant="outline">Current</Badge>}
                              </td>
                              <td className="text-right p-3">{formatCurrency(month.targets?.revenue || 0)}</td>
                              <td className="text-right p-3">
                                {month.actuals ? formatCurrency(month.actuals.revenue) : '—'}
                              </td>
                              <td className="text-right p-3">{month.targets?.sales || 0}</td>
                              <td className="text-right p-3">
                                {month.actuals ? month.actuals.sales : '—'}
                              </td>
                              <td className="text-center p-3">
                                {month.is_future ? (
                                  <Badge variant="secondary" className="text-xs">Future</Badge>
                                ) : month.actuals && month.actuals.revenue >= (month.targets?.revenue || 0) ? (
                                  <CheckCircle2 className="h-4 w-4 text-emerald-500 mx-auto" />
                                ) : (
                                  <AlertCircle className="h-4 w-4 text-yellow-500 mx-auto" />
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
              
              {/* ANNUAL GOALS TAB - Editable */}
              {activeTab === 'goals' && (
                <div className="space-y-6">
                  {/* Volume Metrics */}
                  <div>
                    <h4 className="font-semibold mb-3 text-sm uppercase tracking-wide text-muted-foreground">Volume Targets</h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      {volumeMetrics.map(({ key, label, icon: Icon }) => (
                        <div key={key} className="p-4 rounded-lg border bg-muted/20 space-y-2">
                          <Label className="text-sm flex items-center gap-2">
                            <Icon className="h-4 w-4" />
                            {label}
                          </Label>
                          {isEditing ? (
                            <div className="space-y-2">
                              <div>
                                <span className="text-xs text-muted-foreground">Target</span>
                                <Input
                                  type="number"
                                  value={annualTargets?.[key] || ''}
                                  onChange={(e) => handleTargetChange(key, e.target.value)}
                                  className="h-8"
                                  data-testid={`year-${key}-target`}
                                />
                              </div>
                              <div>
                                <span className="text-xs text-orange-500">Minimum</span>
                                <Input
                                  type="number"
                                  value={annualMins?.[key] || ''}
                                  onChange={(e) => handleMinChange(key, e.target.value)}
                                  className="h-8 border-orange-200"
                                  data-testid={`year-${key}-min`}
                                />
                              </div>
                            </div>
                          ) : (
                            <div>
                              <p className="text-lg font-bold">{formatNumber(annualTargets?.[key] || 0)}</p>
                              <p className="text-xs text-orange-500">Min: {formatNumber(annualMins?.[key] || 0)}</p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Revenue Metrics */}
                  <div>
                    <h4 className="font-semibold mb-3 text-sm uppercase tracking-wide text-muted-foreground">Revenue Targets</h4>
                    <div className="grid grid-cols-2 gap-4">
                      {revenueMetrics.map(({ key, label, icon: Icon }) => (
                        <div key={key} className="p-4 rounded-lg border bg-emerald-500/5 border-emerald-500/20 space-y-2">
                          <Label className="text-sm flex items-center gap-2">
                            <Icon className="h-4 w-4 text-emerald-500" />
                            {label}
                          </Label>
                          {isEditing ? (
                            <div className="space-y-2">
                              <div>
                                <span className="text-xs text-muted-foreground">Target</span>
                                <Input
                                  type="number"
                                  value={annualTargets?.[key] || ''}
                                  onChange={(e) => handleTargetChange(key, e.target.value)}
                                  className="h-8"
                                  placeholder={key === 'revenue_collected' && !annualTargets?.[key] ? String(Math.round((annualTargets?.revenue || 0) * 0.85)) : ''}
                                  data-testid={`year-${key}-target`}
                                />
                                <p className="text-xs text-muted-foreground mt-1">{formatCurrency(effectiveAnnualTargets?.[key] || 0)}</p>
                              </div>
                              <div>
                                <span className="text-xs text-orange-500">Minimum</span>
                                <Input
                                  type="number"
                                  value={annualMins?.[key] || ''}
                                  onChange={(e) => handleMinChange(key, e.target.value)}
                                  className="h-8 border-orange-200"
                                  data-testid={`year-${key}-min`}
                                />
                              </div>
                            </div>
                          ) : (
                            <div>
                              <p className="text-xl font-bold text-emerald-600">{formatCompact(effectiveAnnualTargets?.[key] || 0)}</p>
                              <p className="text-xs text-orange-500">Min: {formatCompact(annualMins?.[key] || 0)}</p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Marketing Efficiency Metrics */}
                  <div>
                    <h4 className="font-semibold mb-3 text-sm uppercase tracking-wide text-muted-foreground">Marketing Efficiency</h4>
                    <div className="grid grid-cols-3 gap-4">
                      {marketingMetrics.map(({ key, label, icon: Icon, isCurrency, isMultiple }) => (
                        <div key={key} className="p-4 rounded-lg border bg-blue-500/5 border-blue-500/20 space-y-2">
                          <Label className="text-sm flex items-center gap-2">
                            <Icon className="h-4 w-4 text-blue-500" />
                            {label}
                          </Label>
                          {isEditing ? (
                            <div className="space-y-2">
                              <div>
                                <span className="text-xs text-muted-foreground">Target</span>
                                <Input
                                  type="number"
                                  step={isMultiple ? "0.1" : "1"}
                                  value={annualTargets?.[key] || ''}
                                  onChange={(e) => handleTargetChange(key, e.target.value)}
                                  className="h-8"
                                  data-testid={`year-${key}-target`}
                                />
                              </div>
                              <div>
                                <span className="text-xs text-orange-500">{key === 'ad_spend' ? 'Max' : 'Minimum'}</span>
                                <Input
                                  type="number"
                                  step={isMultiple ? "0.1" : "1"}
                                  value={annualMins?.[key] || ''}
                                  onChange={(e) => handleMinChange(key, e.target.value)}
                                  className="h-8 border-orange-200"
                                  data-testid={`year-${key}-min`}
                                />
                              </div>
                            </div>
                          ) : (
                            <div>
                              <p className="text-lg font-bold">
                                {isMultiple 
                                  ? `${(annualTargets?.[key] || 0).toFixed(1)}x`
                                  : isCurrency 
                                    ? formatCompact(annualTargets?.[key] || 0)
                                    : formatNumber(annualTargets?.[key] || 0)
                                }
                              </p>
                              <p className="text-xs text-orange-500">
                                {key === 'ad_spend' ? 'Max' : 'Min'}: {isMultiple 
                                  ? `${(annualMins?.[key] || 0).toFixed(1)}x`
                                  : isCurrency 
                                    ? formatCompact(annualMins?.[key] || 0)
                                    : formatNumber(annualMins?.[key] || 0)
                                }
                              </p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              
              {/* BY MARKET TAB - Editable */}
              {activeTab === 'markets' && (
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold">Annual Revenue by Market</h4>
                    <Badge variant="outline" className="text-emerald-600 border-emerald-300">
                      Total: {formatCurrency(Object.values(annualMarketTargets || {}).reduce((sum, m) => sum + (m?.revenue || 0), 0))}
                    </Badge>
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {marketsList.map((market) => (
                      <div key={market.key} className="p-4 rounded-lg border bg-muted/20 space-y-3">
                        <Label className="font-medium flex items-center gap-2">
                          <span>{market.icon}</span>
                          {market.label}
                        </Label>
                        
                        {isEditing ? (
                          <div className="space-y-2">
                            <div>
                              <span className="text-xs text-muted-foreground">Revenue Target</span>
                              <Input
                                type="number"
                                step="100000"
                                value={annualMarketTargets?.[market.key]?.revenue || ''}
                                onChange={(e) => onAnnualMarketTargetsChange?.({
                                  ...annualMarketTargets,
                                  [market.key]: {
                                    ...annualMarketTargets?.[market.key],
                                    revenue: parseFloat(e.target.value) || 0
                                  }
                                })}
                                className="h-8"
                                data-testid={`year-market-${market.key}-revenue`}
                              />
                            </div>
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-muted-foreground">% of Total:</span>
                              <span className="font-medium">
                                {annualTargets?.revenue > 0 
                                  ? ((annualMarketTargets?.[market.key]?.revenue || 0) / annualTargets.revenue * 100).toFixed(0)
                                  : 0}%
                              </span>
                            </div>
                          </div>
                        ) : (
                          <div>
                            <p className="text-xl font-bold text-emerald-600">
                              {formatCompact(annualMarketTargets?.[market.key]?.revenue || 0)}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {annualTargets?.revenue > 0 
                                ? ((annualMarketTargets?.[market.key]?.revenue || 0) / annualTargets.revenue * 100).toFixed(0)
                                : 0}% of company goal
                            </p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  
                  <p className="text-xs text-muted-foreground">
                    Per-market revenue targets for tracking regional performance against the company goal.
                  </p>
                </div>
              )}
            </ScrollArea>
          </>
        )}
        
        <DialogFooter className="border-t pt-4">
          <Button variant="outline" onClick={onClose}>Close</Button>
          {canEdit && isEditing && (
            <Button onClick={() => { onSave?.(); setIsEditing(false); }} data-testid="save-year-goals-btn">
              <Save className="h-4 w-4 mr-2" />
              Save Changes
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Main Monthly Targets Editor Modal
const MonthlyTargetsEditor = ({ 
  isOpen, 
  onClose, 
  onSave, 
  user,
  canEdit = true,
  selectedMarket: propSelectedMarket = 'all',
  markets: propMarkets = [],
  realtimeMetrics = {}
}) => {
  const [viewMode, setViewMode] = useState('monthly'); // 'monthly' or 'weekly'
  const [activeMonth, setActiveMonth] = useState(() => {
    const currentMonth = new Date().getMonth();
    return MONTHS[currentMonth].key;
  });
  const [activeWeek, setActiveWeek] = useState(() => {
    // Calculate current week of year
    const now = new Date();
    const start = new Date(now.getFullYear(), 0, 1);
    const diff = now - start;
    const oneWeek = 1000 * 60 * 60 * 24 * 7;
    return `week_${Math.ceil(diff / oneWeek)}`;
  });
  // Use prop market if provided, otherwise use internal state
  const selectedMarket = propSelectedMarket === 'all' ? 'company' : propSelectedMarket;
  const markets = propMarkets;
  const [monthlyTargets, setMonthlyTargets] = useState({});
  const [weeklyTargets, setWeeklyTargets] = useState({}); // { week_1: { leads, ... }, ... }
  const [marketTargets, setMarketTargets] = useState({}); // { market_id: { monthly: {}, weekly: {} } }
  const [annualTargets, setAnnualTargets] = useState({
    leads: 10404,
    appointments: 4548,
    demos: 3072,
    sales: 1200,
    revenue: 25000000,
    spend: 4344000
  });
  const [annualMins, setAnnualMins] = useState({
    leads: 10404,  // Default same as target
    appointments: 4548,
    demos: 3072,
    sales: 1200,
    revenue: 25000000,  // $25M minimum
    spend: 4344000
  });
  // Efficiency thresholds for Marketing Efficiency Block
  const [efficiencyThresholds, setEfficiencyThresholds] = useState({
    cpl: { target: 404.57, max: 484.94 },
    cps: { target: 909.81, max: 1189.64 },
    cpsale: { target: 3486.24, max: 4621.82 },
    roas: { target: 6.66, min: 5.0 },
    com: { target: 15.0, max: 20.0 },
    nsli: { target: 8000.0, min: 6000.0 }
  });
  
  // Annual market targets (per-market revenue breakdown)
  const [annualMarketTargets, setAnnualMarketTargets] = useState({
    jacksonville: { revenue: 6000000, percentage: 24 },
    orlando: { revenue: 8500000, percentage: 34 },
    tampa: { revenue: 6000000, percentage: 24 },
    'west-palm': { revenue: 2500000, percentage: 10 },
    naples: { revenue: 1000000, percentage: 4 },
    gainesville: { revenue: 1000000, percentage: 4 }
  });
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showYearView, setShowYearView] = useState(false);
  const [yearViewData, setYearViewData] = useState(null);
  const [yearViewLoading, setYearViewLoading] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [updatedByName, setUpdatedByName] = useState(null);
  
  useEffect(() => {
    if (isOpen) {
      fetchTargets();
    }
  }, [isOpen]);
  
  // Removed fetchMarkets since markets are passed as props now
  
  const fetchTargets = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/api/marketing/pacing/targets`);
      const data = response.data;
      
      // Set monthly targets (ensure all months exist)
      const targets = { ...data.monthly_targets };
      MONTHS.forEach(month => {
        if (!targets[month.key]) {
          targets[month.key] = { ...BASELINE_MONTHLY_TARGETS };
        }
      });
      setMonthlyTargets(targets);
      
      // Set weekly targets (ensure all weeks exist)
      const weekTargets = { ...(data.weekly_targets || {}) };
      WEEKS.forEach(week => {
        if (!weekTargets[week.key]) {
          weekTargets[week.key] = { ...BASELINE_WEEKLY_TARGETS };
        }
      });
      setWeeklyTargets(weekTargets);
      
      // Set market targets if available
      if (data.market_targets) {
        setMarketTargets(data.market_targets);
      }
      
      // Set annual targets
      setAnnualTargets({
        leads: data.annual_leads || 10404,
        appointments: data.annual_appointments || 4548,
        demos: data.annual_demos || 3072,
        sales: data.annual_sales || 1200,
        revenue: data.annual_revenue || 25000000,
        spend: data.annual_spend || 4344000
      });
      
      // Set annual minimums (default to targets if not set)
      setAnnualMins({
        leads: data.annual_leads_min || data.annual_leads || 10404,
        appointments: data.annual_appointments_min || data.annual_appointments || 4548,
        demos: data.annual_demos_min || data.annual_demos || 3072,
        sales: data.annual_sales_min || data.annual_sales || 1200,
        revenue: data.annual_revenue_min || data.annual_revenue || 25000000,
        spend: data.annual_spend_min || data.annual_spend || 4344000
      });
      
      // Set efficiency thresholds if available
      if (data.efficiency_thresholds) {
        setEfficiencyThresholds(data.efficiency_thresholds);
      }
      
      // Set annual market targets if available
      if (data.annual_market_targets) {
        setAnnualMarketTargets(data.annual_market_targets);
      }
      
      setUpdatedAt(data.updated_at);
      setUpdatedByName(data.updated_by_name);
    } catch (error) {
      console.error('Error fetching targets:', error);
      toast.error('Failed to load targets');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchYearView = async () => {
    setYearViewLoading(true);
    try {
      const response = await axios.get(`${API}/api/marketing/pacing/year-view`);
      setYearViewData(response.data);
    } catch (error) {
      console.error('Error fetching year view:', error);
      toast.error('Failed to load year view data');
    } finally {
      setYearViewLoading(false);
    }
  };
  
  const handleOpenYearView = () => {
    setShowYearView(true);
    fetchYearView();
  };
  
  // Get current targets based on selected market and view mode
  const getCurrentTargets = () => {
    const baselineTargets = viewMode === 'monthly' ? BASELINE_MONTHLY_TARGETS : BASELINE_WEEKLY_TARGETS;
    const activePeriod = viewMode === 'monthly' ? activeMonth : activeWeek;
    const periodTargets = viewMode === 'monthly' ? monthlyTargets : weeklyTargets;
    
    if (selectedMarket === 'company') {
      return periodTargets[activePeriod] || baselineTargets;
    }
    // For market-specific targets
    const marketData = marketTargets[selectedMarket];
    if (marketData) {
      const marketPeriodTargets = viewMode === 'monthly' ? marketData.monthly : marketData.weekly;
      if (marketPeriodTargets?.[activePeriod]) {
        return marketPeriodTargets[activePeriod];
      }
    }
    return periodTargets[activePeriod] || baselineTargets;
  };
  
  const handleSave = async () => {
    if (!canEdit) return;
    
    setSaving(true);
    try {
      const payload = {
        monthly_targets: monthlyTargets,
        weekly_targets: weeklyTargets,
        market_targets: marketTargets,
        annual_leads: annualTargets.leads,
        annual_appointments: annualTargets.appointments,
        annual_demos: annualTargets.demos,
        annual_sales: annualTargets.sales,
        annual_revenue: annualTargets.revenue,
        annual_spend: annualTargets.spend,
        // Annual minimums
        annual_leads_min: annualMins.leads,
        annual_appointments_min: annualMins.appointments,
        annual_demos_min: annualMins.demos,
        annual_sales_min: annualMins.sales,
        annual_revenue_min: annualMins.revenue,
        annual_spend_min: annualMins.spend,
        // Efficiency thresholds
        efficiency_thresholds: efficiencyThresholds,
        // Per-market annual targets
        annual_market_targets: annualMarketTargets
      };
      
      await axios.put(
        `${API}/api/marketing/pacing/targets?user_id=${user?.id || 'unknown'}&user_name=${encodeURIComponent((user?.first_name || '') + ' ' + (user?.last_name || '') || 'Unknown')}`,
        payload
      );
      
      toast.success('Targets saved successfully');
      onSave?.();
      onClose();
    } catch (error) {
      console.error('Error saving targets:', error);
      toast.error('Failed to save targets');
    } finally {
      setSaving(false);
    }
  };
  
  const handlePeriodChange = (field, value) => {
    const activePeriod = viewMode === 'monthly' ? activeMonth : activeWeek;
    const baselineTargets = viewMode === 'monthly' ? BASELINE_MONTHLY_TARGETS : BASELINE_WEEKLY_TARGETS;
    
    if (selectedMarket === 'company') {
      if (viewMode === 'monthly') {
        setMonthlyTargets(prev => ({
          ...prev,
          [activePeriod]: {
            ...prev[activePeriod],
            [field]: value
          }
        }));
      } else {
        setWeeklyTargets(prev => ({
          ...prev,
          [activePeriod]: {
            ...prev[activePeriod],
            [field]: value
          }
        }));
      }
    } else {
      // Update market-specific targets
      setMarketTargets(prev => {
        const marketData = prev[selectedMarket] || { monthly: {}, weekly: {} };
        const periodType = viewMode === 'monthly' ? 'monthly' : 'weekly';
        const existingPeriodTargets = marketData[periodType]?.[activePeriod] || 
          (viewMode === 'monthly' ? monthlyTargets[activePeriod] : weeklyTargets[activePeriod]) || 
          baselineTargets;
        
        return {
          ...prev,
          [selectedMarket]: {
            ...marketData,
            [periodType]: {
              ...(marketData[periodType] || {}),
              [activePeriod]: {
                ...existingPeriodTargets,
                [field]: value
              }
            }
          }
        };
      });
    }
  };
  
  // Keep old function name for compatibility
  const handleMonthChange = handlePeriodChange;
  
  const handleCopyToAllPeriods = () => {
    const currentTargets = getCurrentTargets();
    const periods = viewMode === 'monthly' ? MONTHS : WEEKS;
    const periodLabel = viewMode === 'monthly' ? 'months' : 'weeks';
    
    if (selectedMarket === 'company') {
      const newTargets = {};
      periods.forEach(period => {
        newTargets[period.key] = { ...currentTargets };
      });
      
      if (viewMode === 'monthly') {
        setMonthlyTargets(newTargets);
      } else {
        setWeeklyTargets(newTargets);
      }
    } else {
      // Copy to all periods for this market
      const newPeriodTargets = {};
      periods.forEach(period => {
        newPeriodTargets[period.key] = { ...currentTargets };
      });
      
      setMarketTargets(prev => {
        const marketData = prev[selectedMarket] || { monthly: {}, weekly: {} };
        return {
          ...prev,
          [selectedMarket]: {
            ...marketData,
            [viewMode === 'monthly' ? 'monthly' : 'weekly']: newPeriodTargets
          }
        };
      });
    }
    
    const activePeriodName = viewMode === 'monthly' 
      ? MONTHS.find(m => m.key === activeMonth)?.name 
      : WEEKS.find(w => w.key === activeWeek)?.name;
    toast.success(`Copied ${activePeriodName} targets to all ${periodLabel}`);
  };
  
  // Keep old function name for compatibility
  const handleCopyToAllMonths = handleCopyToAllPeriods;
  
  const handleCopyCompanyToMarket = () => {
    if (selectedMarket === 'company') return;
    
    // Copy all company monthly and weekly targets to this market
    setMarketTargets(prev => ({
      ...prev,
      [selectedMarket]: {
        monthly: { ...monthlyTargets },
        weekly: { ...weeklyTargets }
      }
    }));
    toast.success('Copied company targets to this market');
  };
  
  const navigatePeriod = (direction) => {
    if (viewMode === 'monthly') {
      const currentIdx = MONTHS.findIndex(m => m.key === activeMonth);
      let newIdx = currentIdx + direction;
      if (newIdx < 0) newIdx = 11;
      if (newIdx > 11) newIdx = 0;
      setActiveMonth(MONTHS[newIdx].key);
    } else {
      const currentIdx = WEEKS.findIndex(w => w.key === activeWeek);
      let newIdx = currentIdx + direction;
      if (newIdx < 0) newIdx = 51;
      if (newIdx > 51) newIdx = 0;
      setActiveWeek(WEEKS[newIdx].key);
    }
  };
  
  // Keep old function name for compatibility
  const navigateMonth = navigatePeriod;
  
  const currentTargets = getCurrentTargets();
  const activePeriodName = viewMode === 'monthly' 
    ? MONTHS.find(m => m.key === activeMonth)?.name || activeMonth
    : WEEKS.find(w => w.key === activeWeek)?.name || activeWeek;
  const selectedMarketName = selectedMarket === 'company' 
    ? 'Company-Wide' 
    : markets.find(m => m.id === selectedMarket || m.slug === selectedMarket)?.name || selectedMarket;
  
  // Calculate sum of monthly targets for comparison with annual
  const monthlySum = useMemo(() => {
    return MONTHS.reduce((acc, month) => {
      const t = monthlyTargets[month.key] || {};
      return {
        leads: acc.leads + (t.leads || 0),
        appointments: acc.appointments + (t.appointments || 0),
        demos: acc.demos + (t.demos || 0),
        sales: acc.sales + (t.sales || 0),
        revenue: acc.revenue + (t.revenue || 0),
        spend: acc.spend + (t.spend || 0)
      };
    }, { leads: 0, appointments: 0, demos: 0, sales: 0, revenue: 0, spend: 0 });
  }, [monthlyTargets]);
  
  // Get current week number for highlighting
  const currentWeekNum = useMemo(() => {
    const now = new Date();
    const start = new Date(now.getFullYear(), 0, 1);
    const diff = now - start;
    const oneWeek = 1000 * 60 * 60 * 24 * 7;
    return Math.ceil(diff / oneWeek);
  }, []);
  
  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col" data-testid="monthly-targets-editor">
          <DialogHeader className="flex-shrink-0">
            <div className="flex items-center justify-between pr-8">
              <DialogTitle className="flex items-center gap-2">
                <Target className="h-5 w-5" />
                Company Targets
              </DialogTitle>
              <div className="flex items-center gap-2">
                {/* View Mode Toggle */}
                <Tabs value={viewMode} onValueChange={setViewMode} className="h-8">
                  <TabsList className="h-8">
                    <TabsTrigger value="monthly" className="h-7 text-xs px-3" data-testid="view-mode-monthly">
                      <CalendarDays className="h-3 w-3 mr-1" />
                      Monthly
                    </TabsTrigger>
                    <TabsTrigger value="weekly" className="h-7 text-xs px-3" data-testid="view-mode-weekly">
                      <CalendarRange className="h-3 w-3 mr-1" />
                      Weekly
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleOpenYearView}
                  data-testid="open-year-view-btn"
                >
                  <Eye className="h-4 w-4 mr-2" />
                  Year View
                </Button>
              </div>
            </div>
          </DialogHeader>
          
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto space-y-4 pr-2">
              {/* Current Target Level Indicator */}
              <div className="flex items-center gap-2 p-3 bg-blue-50 dark:bg-blue-950/30 rounded-lg border border-blue-200 dark:border-blue-800">
                <Building2 className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-700 dark:text-blue-300">Target Level:</span>
                <Badge variant="outline" className="bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 border-blue-300">
                  {selectedMarket === 'company' 
                    ? 'Company-Wide' 
                    : markets.find(m => m.slug === selectedMarket || m.id === selectedMarket)?.name || selectedMarket
                  }
                </Badge>
                <span className="text-xs text-muted-foreground ml-auto">
                  (Change using the market filter at the top of the page)
                </span>
                {selectedMarket !== 'company' && (
                  <Button variant="ghost" size="sm" onClick={handleCopyCompanyToMarket} className="text-xs">
                    <Copy className="h-3 w-3 mr-1" />
                    Copy from Company
                  </Button>
                )}
              </div>
              
              {/* Period Tabs (Month or Week) */}
              <div className="flex items-center gap-2 sticky top-0 bg-background/95 backdrop-blur-sm z-10 py-2">
                <Button variant="ghost" size="icon" onClick={() => navigatePeriod(-1)}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <ScrollArea className="flex-1">
                  {viewMode === 'monthly' ? (
                    <div className="flex gap-1 pb-2">
                      {MONTHS.map((month) => {
                        const currentMonth = new Date().getMonth();
                        const isCurrentMonth = MONTHS[currentMonth].key === month.key;
                        return (
                          <button
                            key={month.key}
                            onClick={() => setActiveMonth(month.key)}
                            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                              activeMonth === month.key
                                ? 'bg-primary text-primary-foreground'
                                : isCurrentMonth
                                ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-200'
                                : 'bg-muted hover:bg-muted/80 text-muted-foreground'
                            }`}
                            data-testid={`month-tab-${month.key}`}
                          >
                            {month.short}
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="flex gap-1 pb-2">
                      {WEEKS.map((week) => {
                        const isCurrentWeek = week.number === currentWeekNum;
                        return (
                          <button
                            key={week.key}
                            onClick={() => setActiveWeek(week.key)}
                            className={`px-2 py-1.5 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                              activeWeek === week.key
                                ? 'bg-primary text-primary-foreground'
                                : isCurrentWeek
                                ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-200'
                                : 'bg-muted hover:bg-muted/80 text-muted-foreground'
                            }`}
                            data-testid={`week-tab-${week.key}`}
                          >
                            {week.short}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </ScrollArea>
                <Button variant="ghost" size="icon" onClick={() => navigatePeriod(1)}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
              
              {/* Period Header */}
              <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                <div>
                  <h4 className="font-semibold text-lg">{activePeriodName} Targets</h4>
                  <p className="text-xs text-muted-foreground">{selectedMarketName}</p>
                </div>
                {canEdit && (
                  <Button variant="outline" size="sm" onClick={handleCopyToAllPeriods}>
                    <Copy className="h-4 w-4 mr-2" />
                    Copy to All {viewMode === 'monthly' ? 'Months' : 'Weeks'}
                  </Button>
                )}
              </div>
              
              <div className="pr-2">
                {/* Volume Targets */}
                <div className="space-y-4">
                  <h5 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">Volume Targets</h5>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2 text-sm">
                        <Users className="h-4 w-4 text-blue-500" />
                        Leads
                      </Label>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <span className="text-xs text-muted-foreground">Target</span>
                          <Input
                            type="number"
                            value={currentTargets.leads || ''}
                            onChange={(e) => handleMonthChange('leads', parseInt(e.target.value) || 0)}
                            disabled={!canEdit}
                            data-testid="leads-input"
                            className="h-8"
                          />
                        </div>
                        <div>
                          <span className="text-xs text-orange-500">Minimum</span>
                          <Input
                            type="number"
                            value={currentTargets.leads_min || ''}
                            onChange={(e) => handleMonthChange('leads_min', parseInt(e.target.value) || 0)}
                            disabled={!canEdit}
                            data-testid="leads-min-input"
                            className="h-8 border-orange-200 focus:border-orange-400"
                          />
                        </div>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2 text-sm">
                        <Calendar className="h-4 w-4 text-green-500" />
                        Appointments
                      </Label>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <span className="text-xs text-muted-foreground">Target</span>
                          <Input
                            type="number"
                            value={currentTargets.appointments || ''}
                            onChange={(e) => handleMonthChange('appointments', parseInt(e.target.value) || 0)}
                            disabled={!canEdit}
                            data-testid="appointments-input"
                            className="h-8"
                          />
                        </div>
                        <div>
                          <span className="text-xs text-orange-500">Minimum</span>
                          <Input
                            type="number"
                            value={currentTargets.appointments_min || ''}
                            onChange={(e) => handleMonthChange('appointments_min', parseInt(e.target.value) || 0)}
                            disabled={!canEdit}
                            data-testid="appointments-min-input"
                            className="h-8 border-orange-200 focus:border-orange-400"
                          />
                        </div>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2 text-sm">
                        <Target className="h-4 w-4 text-purple-500" />
                        Demos
                      </Label>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <span className="text-xs text-muted-foreground">Target</span>
                          <Input
                            type="number"
                            value={currentTargets.demos || ''}
                            onChange={(e) => handleMonthChange('demos', parseInt(e.target.value) || 0)}
                            disabled={!canEdit}
                            data-testid="demos-input"
                            className="h-8"
                          />
                        </div>
                        <div>
                          <span className="text-xs text-orange-500">Minimum</span>
                          <Input
                            type="number"
                            value={currentTargets.demos_min || ''}
                            onChange={(e) => handleMonthChange('demos_min', parseInt(e.target.value) || 0)}
                            disabled={!canEdit}
                            data-testid="demos-min-input"
                            className="h-8 border-orange-200 focus:border-orange-400"
                          />
                        </div>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2 text-sm">
                        <TrendingUp className="h-4 w-4 text-emerald-500" />
                        Sales
                      </Label>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <span className="text-xs text-muted-foreground">Target</span>
                          <Input
                            type="number"
                            value={currentTargets.sales || ''}
                            onChange={(e) => handleMonthChange('sales', parseInt(e.target.value) || 0)}
                            disabled={!canEdit}
                            data-testid="sales-input"
                            className="h-8"
                          />
                        </div>
                        <div>
                          <span className="text-xs text-orange-500">Minimum</span>
                          <Input
                            type="number"
                            value={currentTargets.sales_min || ''}
                            onChange={(e) => handleMonthChange('sales_min', parseInt(e.target.value) || 0)}
                            disabled={!canEdit}
                            data-testid="sales-min-input"
                            className="h-8 border-orange-200 focus:border-orange-400"
                          />
                        </div>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2 text-sm">
                        <DollarSign className="h-4 w-4 text-yellow-500" />
                        Revenue
                      </Label>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <span className="text-xs text-muted-foreground">Target</span>
                          <Input
                            type="number"
                            value={currentTargets.revenue || ''}
                            onChange={(e) => handleMonthChange('revenue', parseFloat(e.target.value) || 0)}
                            disabled={!canEdit}
                            data-testid="revenue-input"
                            className="h-8"
                          />
                          <p className="text-xs text-muted-foreground">{formatCurrency(currentTargets.revenue)}</p>
                        </div>
                        <div>
                          <span className="text-xs text-orange-500">Minimum</span>
                          <Input
                            type="number"
                            value={currentTargets.revenue_min || ''}
                            onChange={(e) => handleMonthChange('revenue_min', parseFloat(e.target.value) || 0)}
                            disabled={!canEdit}
                            data-testid="revenue-min-input"
                            className="h-8 border-orange-200 focus:border-orange-400"
                          />
                          <p className="text-xs text-orange-500">{formatCurrency(currentTargets.revenue_min)}</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2 text-sm">
                        <DollarSign className="h-4 w-4 text-red-500" />
                        Ad Spend
                      </Label>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <span className="text-xs text-muted-foreground">Budget</span>
                          <Input
                            type="number"
                            value={currentTargets.spend || ''}
                            onChange={(e) => handleMonthChange('spend', parseFloat(e.target.value) || 0)}
                            disabled={!canEdit}
                            data-testid="spend-input"
                            className="h-8"
                          />
                        </div>
                        <div>
                          <span className="text-xs text-red-500">Max Allowed</span>
                          <Input
                            type="number"
                            value={currentTargets.spend_max || ''}
                            onChange={(e) => handleMonthChange('spend_max', parseFloat(e.target.value) || 0)}
                            disabled={!canEdit}
                            data-testid="spend-max-input"
                            className="h-8 border-red-200 focus:border-red-400"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Rate Targets */}
                  <h5 className="font-medium text-sm text-muted-foreground uppercase tracking-wide mt-6">Rate Targets (%)</h5>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="space-y-2">
                      <Label className="text-sm">Set Rate</Label>
                      <Input
                        type="number"
                        step="0.1"
                        value={currentTargets.set_rate || ''}
                        onChange={(e) => handleMonthChange('set_rate', parseFloat(e.target.value) || 0)}
                        disabled={!canEdit}
                        data-testid="set-rate-input"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label className="text-sm">Issue Rate</Label>
                      <Input
                        type="number"
                        step="0.1"
                        value={currentTargets.issue_rate || ''}
                        onChange={(e) => handleMonthChange('issue_rate', parseFloat(e.target.value) || 0)}
                        disabled={!canEdit}
                        data-testid="issue-rate-input"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label className="text-sm">Demo Rate</Label>
                      <Input
                        type="number"
                        step="0.1"
                        value={currentTargets.demo_rate || ''}
                        onChange={(e) => handleMonthChange('demo_rate', parseFloat(e.target.value) || 0)}
                        disabled={!canEdit}
                        data-testid="demo-rate-input"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label className="text-sm">Close Rate</Label>
                      <Input
                        type="number"
                        step="0.1"
                        value={currentTargets.close_rate || ''}
                        onChange={(e) => handleMonthChange('close_rate', parseFloat(e.target.value) || 0)}
                        disabled={!canEdit}
                        data-testid="close-rate-input"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-sm">Average Ticket</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={currentTargets.average_ticket || ''}
                        onChange={(e) => handleMonthChange('average_ticket', parseFloat(e.target.value) || 0)}
                        disabled={!canEdit}
                        data-testid="avg-ticket-input"
                      />
                      <p className="text-xs text-muted-foreground">{formatCurrency(currentTargets.average_ticket)}</p>
                    </div>
                    
                    <div className="space-y-2">
                      <Label className="text-sm">Issued</Label>
                      <Input
                        type="number"
                        value={currentTargets.issued || ''}
                        onChange={(e) => handleMonthChange('issued', parseInt(e.target.value) || 0)}
                        disabled={!canEdit}
                        data-testid="issued-input"
                      />
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Annual Summary */}
              <div className="p-4 bg-gradient-to-r from-primary/5 to-primary/10 rounded-lg border border-primary/20">
                <div className="flex items-center justify-between mb-2">
                  <h5 className="font-medium">Annual Target Summary</h5>
                  <Badge variant="outline">$25M Goal</Badge>
                </div>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Monthly Target</p>
                    <p className="font-semibold text-lg">{formatCurrency(annualTargets.revenue / 12)}</p>
                    <p className="text-xs text-muted-foreground">to hit $25M/year</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Annual Target</p>
                    <p className="font-semibold">{formatCurrency(annualTargets.revenue)}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">12-Month Sum</p>
                    <p className={`font-semibold ${monthlySum.revenue >= annualTargets.revenue ? 'text-emerald-600' : 'text-yellow-600'}`}>
                      {formatCurrency(monthlySum.revenue)}
                    </p>
                  </div>
                </div>
              </div>
              
              {/* ============================================ */}
              {/* EFFICIENCY THRESHOLDS SECTION */}
              {/* ============================================ */}
              <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/30 dark:to-indigo-950/30 rounded-lg border border-blue-200 dark:border-blue-800">
                <div className="flex items-center gap-2 mb-4">
                  <Gauge className="h-5 w-5 text-blue-600" />
                  <h5 className="font-semibold text-blue-800 dark:text-blue-200">Marketing Efficiency Thresholds</h5>
                  <Badge variant="outline" className="ml-auto text-blue-600 border-blue-300">
                    Global Settings
                  </Badge>
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {/* CPL - Cost Per Lead */}
                  <div className="space-y-1">
                    <Label className="text-xs font-medium text-blue-700 dark:text-blue-300">
                      CPL (Cost/Lead)
                    </Label>
                    <div className="flex gap-2">
                      <div className="flex-1">
                        <p className="text-[10px] text-muted-foreground">Target</p>
                        <Input
                          type="number"
                          step="0.01"
                          value={efficiencyThresholds.cpl?.target || ''}
                          onChange={(e) => setEfficiencyThresholds(prev => ({
                            ...prev,
                            cpl: { ...prev.cpl, target: parseFloat(e.target.value) || 0 }
                          }))}
                          disabled={!canEdit}
                          className="h-8 text-sm"
                          data-testid="cpl-target-input"
                        />
                      </div>
                      <div className="flex-1">
                        <p className="text-[10px] text-muted-foreground">Max</p>
                        <Input
                          type="number"
                          step="0.01"
                          value={efficiencyThresholds.cpl?.max || ''}
                          onChange={(e) => setEfficiencyThresholds(prev => ({
                            ...prev,
                            cpl: { ...prev.cpl, max: parseFloat(e.target.value) || 0 }
                          }))}
                          disabled={!canEdit}
                          className="h-8 text-sm"
                          data-testid="cpl-max-input"
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* CPS - Cost Per Schedule */}
                  <div className="space-y-1">
                    <Label className="text-xs font-medium text-blue-700 dark:text-blue-300">
                      CPS (Cost/Schedule)
                    </Label>
                    <div className="flex gap-2">
                      <div className="flex-1">
                        <p className="text-[10px] text-muted-foreground">Target</p>
                        <Input
                          type="number"
                          step="0.01"
                          value={efficiencyThresholds.cps?.target || ''}
                          onChange={(e) => setEfficiencyThresholds(prev => ({
                            ...prev,
                            cps: { ...prev.cps, target: parseFloat(e.target.value) || 0 }
                          }))}
                          disabled={!canEdit}
                          className="h-8 text-sm"
                          data-testid="cps-target-input"
                        />
                      </div>
                      <div className="flex-1">
                        <p className="text-[10px] text-muted-foreground">Max</p>
                        <Input
                          type="number"
                          step="0.01"
                          value={efficiencyThresholds.cps?.max || ''}
                          onChange={(e) => setEfficiencyThresholds(prev => ({
                            ...prev,
                            cps: { ...prev.cps, max: parseFloat(e.target.value) || 0 }
                          }))}
                          disabled={!canEdit}
                          className="h-8 text-sm"
                          data-testid="cps-max-input"
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* CPSALE - Cost Per Sale */}
                  <div className="space-y-1">
                    <Label className="text-xs font-medium text-blue-700 dark:text-blue-300">
                      CPSALE (Cost/Sale)
                    </Label>
                    <div className="flex gap-2">
                      <div className="flex-1">
                        <p className="text-[10px] text-muted-foreground">Target</p>
                        <Input
                          type="number"
                          step="0.01"
                          value={efficiencyThresholds.cpsale?.target || ''}
                          onChange={(e) => setEfficiencyThresholds(prev => ({
                            ...prev,
                            cpsale: { ...prev.cpsale, target: parseFloat(e.target.value) || 0 }
                          }))}
                          disabled={!canEdit}
                          className="h-8 text-sm"
                          data-testid="cpsale-target-input"
                        />
                      </div>
                      <div className="flex-1">
                        <p className="text-[10px] text-muted-foreground">Max</p>
                        <Input
                          type="number"
                          step="0.01"
                          value={efficiencyThresholds.cpsale?.max || ''}
                          onChange={(e) => setEfficiencyThresholds(prev => ({
                            ...prev,
                            cpsale: { ...prev.cpsale, max: parseFloat(e.target.value) || 0 }
                          }))}
                          disabled={!canEdit}
                          className="h-8 text-sm"
                          data-testid="cpsale-max-input"
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* ROAS - Return on Ad Spend */}
                  <div className="space-y-1">
                    <Label className="text-xs font-medium text-blue-700 dark:text-blue-300">
                      ROAS (Return/Spend)
                    </Label>
                    <div className="flex gap-2">
                      <div className="flex-1">
                        <p className="text-[10px] text-muted-foreground">Target</p>
                        <Input
                          type="number"
                          step="0.01"
                          value={efficiencyThresholds.roas?.target || ''}
                          onChange={(e) => setEfficiencyThresholds(prev => ({
                            ...prev,
                            roas: { ...prev.roas, target: parseFloat(e.target.value) || 0 }
                          }))}
                          disabled={!canEdit}
                          className="h-8 text-sm"
                          data-testid="roas-target-input"
                        />
                      </div>
                      <div className="flex-1">
                        <p className="text-[10px] text-muted-foreground">Min</p>
                        <Input
                          type="number"
                          step="0.01"
                          value={efficiencyThresholds.roas?.min || ''}
                          onChange={(e) => setEfficiencyThresholds(prev => ({
                            ...prev,
                            roas: { ...prev.roas, min: parseFloat(e.target.value) || 0 }
                          }))}
                          disabled={!canEdit}
                          className="h-8 text-sm"
                          data-testid="roas-min-input"
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* COM % - Cost of Marketing */}
                  <div className="space-y-1">
                    <Label className="text-xs font-medium text-blue-700 dark:text-blue-300">
                      COM % (Cost of Mkt)
                    </Label>
                    <div className="flex gap-2">
                      <div className="flex-1">
                        <p className="text-[10px] text-muted-foreground">Target</p>
                        <Input
                          type="number"
                          step="0.1"
                          value={efficiencyThresholds.com?.target || ''}
                          onChange={(e) => setEfficiencyThresholds(prev => ({
                            ...prev,
                            com: { ...prev.com, target: parseFloat(e.target.value) || 0 }
                          }))}
                          disabled={!canEdit}
                          className="h-8 text-sm"
                          data-testid="com-target-input"
                        />
                      </div>
                      <div className="flex-1">
                        <p className="text-[10px] text-muted-foreground">Max</p>
                        <Input
                          type="number"
                          step="0.1"
                          value={efficiencyThresholds.com?.max || ''}
                          onChange={(e) => setEfficiencyThresholds(prev => ({
                            ...prev,
                            com: { ...prev.com, max: parseFloat(e.target.value) || 0 }
                          }))}
                          disabled={!canEdit}
                          className="h-8 text-sm"
                          data-testid="com-max-input"
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* NSLI - Net Sale Lead Index (Avg Sale) */}
                  <div className="space-y-1">
                    <Label className="text-xs font-medium text-blue-700 dark:text-blue-300">
                      NSLI (Avg Sale $)
                    </Label>
                    <div className="flex gap-2">
                      <div className="flex-1">
                        <p className="text-[10px] text-muted-foreground">Target</p>
                        <Input
                          type="number"
                          step="100"
                          value={efficiencyThresholds.nsli?.target || ''}
                          onChange={(e) => setEfficiencyThresholds(prev => ({
                            ...prev,
                            nsli: { ...prev.nsli, target: parseFloat(e.target.value) || 0 }
                          }))}
                          disabled={!canEdit}
                          className="h-8 text-sm"
                          data-testid="nsli-target-input"
                        />
                      </div>
                      <div className="flex-1">
                        <p className="text-[10px] text-muted-foreground">Min</p>
                        <Input
                          type="number"
                          step="100"
                          value={efficiencyThresholds.nsli?.min || ''}
                          onChange={(e) => setEfficiencyThresholds(prev => ({
                            ...prev,
                            nsli: { ...prev.nsli, min: parseFloat(e.target.value) || 0 }
                          }))}
                          disabled={!canEdit}
                          className="h-8 text-sm"
                          data-testid="nsli-min-input"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                
                <p className="text-[10px] text-blue-600/70 dark:text-blue-400/70 mt-3">
                  These thresholds apply globally to all markets. Used by Marketing Efficiency Block to determine status colors.
                </p>
              </div>
              
              {/* ============================================ */}
              {/* ANNUAL GOALS - Year-End Projection */}
              {/* ============================================ */}
              <YearEndProjection 
                annualTargets={annualTargets}
                annualMins={annualMins}
                onAnnualTargetsChange={setAnnualTargets}
                onAnnualMinsChange={setAnnualMins}
                canEdit={canEdit}
              />
              
              {/* ============================================ */}
              {/* PER-MARKET ANNUAL REVENUE TARGETS */}
              {/* ============================================ */}
              <div className="p-4 bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-950/30 dark:to-teal-950/30 rounded-lg border border-emerald-200 dark:border-emerald-800">
                <div className="flex items-center gap-2 mb-4">
                  <MapPin className="h-5 w-5 text-emerald-600" />
                  <h5 className="font-semibold text-emerald-800 dark:text-emerald-200">Annual Revenue by Market</h5>
                  <Badge variant="outline" className="ml-auto text-emerald-600 border-emerald-300">
                    Total: {formatCurrency(Object.values(annualMarketTargets).reduce((sum, m) => sum + (m.revenue || 0), 0))}
                  </Badge>
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {[
                    { key: 'jacksonville', label: 'Jacksonville', icon: '🦎' },
                    { key: 'orlando', label: 'Orlando', icon: '🏰' },
                    { key: 'tampa', label: 'Tampa', icon: '⚡' },
                    { key: 'west-palm', label: 'West Palm', icon: '🌴' },
                    { key: 'naples', label: 'Naples/Ft. Myers', icon: '🌊' },
                    { key: 'gainesville', label: 'Gainesville', icon: '🐊' }
                  ].map(market => (
                    <div key={market.key} className="space-y-1">
                      <Label className="text-xs font-medium text-emerald-700 dark:text-emerald-300 flex items-center gap-1">
                        <span>{market.icon}</span> {market.label}
                      </Label>
                      <div className="flex gap-2">
                        <div className="flex-1">
                          <p className="text-[10px] text-muted-foreground">Revenue Target</p>
                          <Input
                            type="number"
                            step="100000"
                            value={annualMarketTargets[market.key]?.revenue || ''}
                            onChange={(e) => setAnnualMarketTargets(prev => ({
                              ...prev,
                              [market.key]: { 
                                ...prev[market.key], 
                                revenue: parseFloat(e.target.value) || 0,
                                percentage: annualTargets.revenue > 0 
                                  ? ((parseFloat(e.target.value) || 0) / annualTargets.revenue * 100).toFixed(1)
                                  : 0
                              }
                            }))}
                            disabled={!canEdit}
                            className="h-8 text-sm"
                            data-testid={`market-${market.key}-revenue`}
                          />
                        </div>
                        <div className="w-16">
                          <p className="text-[10px] text-muted-foreground">% Share</p>
                          <div className="h-8 flex items-center justify-center bg-muted rounded-md text-sm font-medium">
                            {annualTargets.revenue > 0 
                              ? ((annualMarketTargets[market.key]?.revenue || 0) / annualTargets.revenue * 100).toFixed(0)
                              : 0}%
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                
                <p className="text-[10px] text-emerald-600/70 dark:text-emerald-400/70 mt-3">
                  Per-market revenue targets for tracking regional performance against the $25M company goal.
                </p>
              </div>
              
              {/* Last Updated */}
              {updatedAt && (
                <div className="flex items-center justify-between text-xs text-muted-foreground pt-2 border-t">
                  <div className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    <span>Last updated: {new Date(updatedAt).toLocaleString()}</span>
                  </div>
                  {updatedByName && <span>by {updatedByName}</span>}
                </div>
              )}
            </div>
          )}
          
          <DialogFooter className="flex-shrink-0 border-t pt-4 mt-4">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            {canEdit && (
              <Button onClick={handleSave} disabled={saving} data-testid="save-targets-btn">
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save All Targets
                  </>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Year View Modal */}
      <YearViewModal
        isOpen={showYearView}
        onClose={() => setShowYearView(false)}
        yearData={yearViewData}
        loading={yearViewLoading}
        annualTargets={annualTargets}
        annualMins={annualMins}
        annualMarketTargets={annualMarketTargets}
        onAnnualTargetsChange={setAnnualTargets}
        onAnnualMinsChange={setAnnualMins}
        onAnnualMarketTargetsChange={setAnnualMarketTargets}
        onSave={handleSave}
        canEdit={canEdit}
        realtimeMetrics={realtimeMetrics}
      />
    </>
  );
};

export default MonthlyTargetsEditor;
